import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { BenchReason } from "@/types/workforce";

interface BenchCandidate {
	id: string;
	workforceId: string; // The workforce entry ID for the on_bench record
	name: string;
	email?: string;
	phone?: string;
	function?: string;
	skills?: string[];
	// Bench info
	benchStartDate: string;
	endDate: string | null;
	lastAssignmentCompany: string | null;
	lastAssignmentRole: string | null;
	daysOnBench: number;
	status: string;
	// Cost info
	dailyCost: number | null; // bench_daily_cost - what you pay them
	currency: string;
	benchCost: number | null; // Total cost since on bench (dailyCost * daysOnBench)
	// Work arrangement
	workPercentage: number | null;
	// Metadata
	totalAssignments: number;
	assignedRecruiter?: string | null;
	// Bench entry info
	benchReason?: BenchReason | null;
	notes?: string | null;
	previousWorkforceId?: string | null;
}

interface BenchStats {
	totalOnBench: number;
	totalDailyBenchCost: number;
	averageDaysOnBench: number;
	longestOnBench: number;
	currency: string;
}

interface StopBenchData {
	reason: string;
	note?: string;
}

export interface UseWorkforceBenchReturn {
	benchCandidates: BenchCandidate[];
	stats: BenchStats;
	isLoading: boolean;
	refetch: () => Promise<void>;
	stopBench: (workforceId: string, data: StopBenchData) => Promise<boolean>;
	reopenBench: (workforceId: string) => Promise<boolean>;
}

export const useWorkforceBench = (): UseWorkforceBenchReturn => {
	const [benchCandidates, setBenchCandidates] = useState<BenchCandidate[]>([]);
	const [stats, setStats] = useState<BenchStats>({
		totalOnBench: 0,
		totalDailyBenchCost: 0,
		averageDaysOnBench: 0,
		longestOnBench: 0,
		currency: 'EUR'
	});
	const [isLoading, setIsLoading] = useState(true);
	const [orgUuid, setOrgUuid] = useState<string | null>(null);

	// Fetch organization UUID
	useEffect(() => {
		const fetchOrgUuid = async () => {
			try {
				const { data: { user } } = await supabase.auth.getUser();
				if (!user) return;

				const { data: userOrg } = await supabase
					.from('user_organization')
					.select('organization_uuid')
					.eq('user_uuid', user.id)
					.single();

				if (userOrg?.organization_uuid)
					setOrgUuid(userOrg.organization_uuid);
			} catch (error) {
				// Silent error
			}
		};

		fetchOrgUuid();
	}, []);

	const fetchBenchCandidates = useCallback(async () => {
		if (!orgUuid) return;

		try {
			setIsLoading(true);

			// Fetch all workforce entries flagged as bench
			const { data: benchData, error: benchError } = await supabase
				.from('workforce')
				.select(`
					id,
					candidate_id,
					start_date,
					end_date,
					status,
					bench_reason,
					bench_daily_cost,
					currency,
					notes,
					previous_workforce_id,
					assigned_recruiter,
					work_percentage,
					candidates(id, name, email, phone, function, skills)
				`)
				.eq('organization_uuid', orgUuid)
				.eq('is_bench', true);

			if (benchError) throw benchError;

			// Get previous assignment info for context
			const previousIds = (benchData || [])
				.map(b => b.previous_workforce_id)
				.filter(Boolean) as string[];

			const previousAssignmentsMap = new Map<string, any>();
			if (previousIds.length > 0) {
				const { data: prevData } = await supabase
					.from('workforce')
					.select(`
						id,
						external_company_name,
						external_job_title,
						jobs(title, companies(name))
					`)
					.in('id', previousIds);

				if (prevData) {
					for (const prev of prevData) {
						previousAssignmentsMap.set(prev.id, prev);
					}
				}
			}

			// Get total assignment count per candidate (excluding bench entries)
			const candidateIds = (benchData || []).map(b => b.candidate_id);
			const assignmentCounts = new Map<string, number>();
			if (candidateIds.length > 0) {
				const { data: countData } = await supabase
					.from('workforce')
					.select('candidate_id')
					.eq('organization_uuid', orgUuid)
					.in('candidate_id', candidateIds)
					.neq('is_bench', true);

				if (countData) {
					for (const entry of countData) {
						const count = assignmentCounts.get(entry.candidate_id) || 0;
						assignmentCounts.set(entry.candidate_id, count + 1);
					}
				}
			}

			// Build bench list
			const today = new Date();
			const benchList: BenchCandidate[] = [];

			for (const entry of benchData || []) {
				if (!entry.candidates) continue;

				const benchStartDate = new Date(entry.start_date);
				const isCompleted = entry.status === 'completed' || entry.status === 'terminated';
				const endDate = isCompleted && entry.end_date ? new Date(entry.end_date) : today;
				const daysOnBench = Math.max(1, Math.floor((endDate.getTime() - benchStartDate.getTime()) / (1000 * 60 * 60 * 24)));
				const dailyCost = entry.bench_daily_cost ?? null;
				const allocation = (entry.work_percentage ?? 100) / 100;
				const benchCost = dailyCost != null ? dailyCost * daysOnBench * allocation : null;

				// Get previous assignment info
				let lastCompany = null;
				let lastRole = null;
				if (entry.previous_workforce_id) {
					const prev = previousAssignmentsMap.get(entry.previous_workforce_id);
					if (prev) {
						lastCompany = prev.jobs?.companies?.name || prev.external_company_name || null;
						lastRole = prev.jobs?.title || prev.external_job_title || null;
					}
				}

				benchList.push({
					id: entry.candidates.id,
					workforceId: entry.id,
					name: entry.candidates.name,
					email: entry.candidates.email,
					phone: entry.candidates.phone,
					function: entry.candidates.function,
					skills: entry.candidates.skills,
					benchStartDate: entry.start_date,
					endDate: entry.end_date || null,
					lastAssignmentCompany: lastCompany,
					lastAssignmentRole: lastRole,
					daysOnBench,
					status: entry.status,
					dailyCost,
					currency: entry.currency || 'EUR',
					benchCost,
					workPercentage: entry.work_percentage,
					totalAssignments: assignmentCounts.get(entry.candidate_id) || 0,
					assignedRecruiter: entry.assigned_recruiter,
					benchReason: entry.bench_reason as BenchReason | null,
					notes: entry.notes,
					previousWorkforceId: entry.previous_workforce_id
				});
			}

			// Sort by days on bench (longest first)
			benchList.sort((a, b) => b.daysOnBench - a.daysOnBench);

			// Calculate stats (only for active bench entries)
			const activeBench = benchList.filter(c => c.status === 'active' || c.status === 'on_hold');
			const totalOnBench = activeBench.length;
			const totalDailyBenchCost = activeBench.reduce((sum, c) => sum + (c.benchCost || 0), 0);
			const totalDays = activeBench.reduce((sum, c) => sum + c.daysOnBench, 0);
			const averageDaysOnBench = totalOnBench > 0 ? Math.round(totalDays / totalOnBench) : 0;
			const longestOnBench = activeBench.length > 0 ? activeBench[0].daysOnBench : 0;

			setBenchCandidates(benchList);
			setStats({
				totalOnBench,
				totalDailyBenchCost,
				averageDaysOnBench,
				longestOnBench,
				currency: 'EUR'
			});
		} catch (error) {
			setBenchCandidates([]);
		} finally {
			setIsLoading(false);
		}
	}, [orgUuid]);

	const stopBench = useCallback(async (workforceId: string, data: StopBenchData): Promise<boolean> => {
		if (!orgUuid) return false;

		try {
			const { error } = await supabase
				.from('workforce')
				.update({
					status: 'completed',
					end_date: new Date().toISOString().split('T')[0],
					status_change_reason: data.reason,
					status_change_note: data.note || null,
				})
				.eq('id', workforceId)
				.eq('organization_uuid', orgUuid)
				.eq('is_bench', true);

			if (error) throw error;

			await fetchBenchCandidates();
			return true;
		} catch (error) {
			return false;
		}
	}, [orgUuid, fetchBenchCandidates]);

	const reopenBench = useCallback(async (workforceId: string): Promise<boolean> => {
		if (!orgUuid) return false;

		try {
			const { error } = await supabase
				.from('workforce')
				.update({
					status: 'active',
					end_date: null,
					status_change_reason: null,
					status_change_note: null,
				})
				.eq('id', workforceId)
				.eq('organization_uuid', orgUuid)
				.eq('is_bench', true);

			if (error) throw error;

			await fetchBenchCandidates();
			return true;
		} catch (error) {
			return false;
		}
	}, [orgUuid, fetchBenchCandidates]);

	useEffect(() => {
		fetchBenchCandidates();
	}, [fetchBenchCandidates]);

	return {
		benchCandidates,
		stats,
		isLoading,
		refetch: fetchBenchCandidates,
		stopBench,
		reopenBench
	};
};
